/**
 * Buy X Get Y Auto Add to Cart
 * Automatically adds free products to cart when conditions are met
 * Compatible with Shopify 2.0 themes
 */

class BuyXGetYAutoAdd {
  constructor() {
    this.config = window.buyXGetYConfig || {};
    this.cart = null;
    this.isProcessing = false;
    
    this.init();
  }

  init() {
    // Wait for DOM to be ready
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => this.setup());
    } else {
      this.setup();
    }
  }

  setup() {
    this.bindEvents();
    this.setupMutationObserver();
    this.setupFetchInterceptor();
    this.checkCartForOffers();
  }


  bindEvents() {
    // Listen for cart updates
    document.addEventListener('cart:updated', () => this.handleCartUpdate());
    document.addEventListener('cart:refresh', () => this.handleCartUpdate());
    
    // Listen for various product added events
    document.addEventListener('ajaxProduct:added', (event) => {
      if (this.config.settings.debug) {
        console.log('Buy X Get Y: ajaxProduct:added event received');
      }
      setTimeout(() => this.handleCartUpdate(), 1500);
    });

    // Listen for form submissions (PDP add to cart)
    document.addEventListener('submit', (event) => {
      if (event.target.matches('form[action*="/cart/add"]') || 
          event.target.closest('form[action*="/cart/add"]')) {
        if (this.config.settings.debug) {
          console.log('Buy X Get Y: Product form submission detected');
        }
        setTimeout(() => this.handleCartUpdate(), 2000);
      }
    });

    // Listen for add-to-cart button clicks
    document.addEventListener('click', (event) => {
      // Collection page add-to-cart buttons
      if (event.target.matches('add-to-cart') || 
          event.target.closest('add-to-cart')) {
        if (this.config.settings.debug) {
          console.log('Buy X Get Y: add-to-cart element clicked');
        }
        setTimeout(() => this.handleCartUpdate(), 1500);
      }
      
      // PDP add to cart buttons
      if (event.target.matches('.product-form__submit') || 
          event.target.closest('.product-form__submit') ||
          event.target.matches('[type="submit"][name="add"]') ||
          event.target.closest('[type="submit"][name="add"]')) {
        if (this.config.settings.debug) {
          console.log('Buy X Get Y: Product form submit button clicked');
        }
        setTimeout(() => this.handleCartUpdate(), 2000);
      }
      
      // Cart item removal
      if (event.target.matches('[data-cart-remove]') || 
          event.target.closest('[data-cart-remove]')) {
        setTimeout(() => this.handleCartUpdate(), 800);
      }
    });

    // Listen for quantity changes
    document.addEventListener('change', (event) => {
      if (event.target.matches('[name="updates[]"]')) {
        setTimeout(() => this.handleCartUpdate(), 800);
      }
    });

    // Listen for custom events that might be dispatched by the theme
    document.addEventListener('product:added-to-cart', (event) => {
      if (this.config.settings.debug) {
        console.log('Buy X Get Y: product:added-to-cart event received');
      }
      setTimeout(() => this.handleCartUpdate(), 1500);
    });

    document.addEventListener('cart:item-added', (event) => {
      if (this.config.settings.debug) {
        console.log('Buy X Get Y: cart:item-added event received');
      }
      setTimeout(() => this.handleCartUpdate(), 1500);
    });
  }

  setupMutationObserver() {
    // Create a MutationObserver to watch for cart item changes
    this.mutationObserver = new MutationObserver((mutations) => {
      let shouldProcess = false;
      
      mutations.forEach((mutation) => {
        // Check if cart items were added or removed
        if (mutation.type === 'childList') {
          mutation.addedNodes.forEach((node) => {
            if (node.nodeType === Node.ELEMENT_NODE) {
              // Check if a cart item was added - specifically looking for .cart-item class
              if (node.matches('.cart-item[data-product-id]') || 
                  node.querySelector('.cart-item[data-product-id]') ||
                  node.matches('[data-product-id]') || 
                  node.querySelector('[data-product-id]')) {
                shouldProcess = true;
                if (this.config.settings.debug) {
                  console.log('Buy X Get Y: MutationObserver detected cart item addition', {
                    node: node.tagName,
                    className: node.className,
                    hasDataProductId: !!node.getAttribute('data-product-id')
                  });
                }
              }
            }
          });
          
          mutation.removedNodes.forEach((node) => {
            if (node.nodeType === Node.ELEMENT_NODE) {
              // Check if a cart item was removed - specifically looking for .cart-item class
              if (node.matches('.cart-item[data-product-id]') || 
                  node.querySelector('.cart-item[data-product-id]') ||
                  node.matches('[data-product-id]') || 
                  node.querySelector('[data-product-id]')) {
                shouldProcess = true;
                if (this.config.settings.debug) {
                  console.log('Buy X Get Y: MutationObserver detected cart item removal', {
                    node: node.tagName,
                    className: node.className,
                    hasDataProductId: !!node.getAttribute('data-product-id')
                  });
                }
              }
            }
          });
        }
      });
      
      if (shouldProcess) {
        // Debounce the processing to avoid too many calls
        clearTimeout(this.mutationTimeout);
        this.mutationTimeout = setTimeout(() => {
          this.handleCartUpdate();
        }, 500);
      }
    });
    
    // Start observing the cart containers - specifically looking for cart-items tag with .cart-item class
    const cartContainers = [
      document.querySelector('cart-items'),
      document.querySelector('.cart-items'),
      document.querySelector('.mini-cart'),
      document.querySelector('[data-cart-items]'),
      document.querySelector('.cart__items')
    ].filter(Boolean);
    
    cartContainers.forEach((container) => {
      if (container) {
        this.mutationObserver.observe(container, {
          childList: true,
          subtree: true
        });
        if (this.config.settings.debug) {
          console.log('Buy X Get Y: MutationObserver started for', container.tagName || container.className);
        }
      }
    });
  }

  setupFetchInterceptor() {
    // Intercept fetch requests to detect cart additions
    const originalFetch = window.fetch;
    const self = this;
    
    window.fetch = function(...args) {
      const [url, options] = args;
      
      // Check if this is a cart add request
      if (typeof url === 'string' && url.includes('/cart/add')) {
        if (self.config.settings.debug) {
          console.log('Buy X Get Y: Cart add request detected via fetch interceptor');
        }
        
        // Return the original fetch but add a handler for the response
        return originalFetch.apply(this, args).then(response => {
          if (response.ok) {
            // Cart add was successful, trigger our handler
            setTimeout(() => {
              if (self.config.settings.debug) {
                console.log('Buy X Get Y: Cart add successful, triggering update');
              }
              self.handleCartUpdate();
            }, 1500);
          }
          return response;
        });
      }
      
      // For other requests, just return the original fetch
      return originalFetch.apply(this, args);
    };
  }

  async handleCartUpdate() {
    if (this.isProcessing) return;
    
    // Prevent too frequent updates
    const now = Date.now();
    if (this.lastUpdateTime && (now - this.lastUpdateTime) < 2000) {
      if (this.config.settings.debug) {
        console.log('Buy X Get Y: Skipping update - too frequent', {
          timeSinceLastUpdate: now - this.lastUpdateTime,
          threshold: 2000
        });
      }
      return;
    }
    this.lastUpdateTime = now;
    
    if (this.config.settings.debug) {
      console.log('Buy X Get Y: Starting cart update');
    }
    
    try {
      this.isProcessing = true;
      await this.fetchCart();
      await this.processOffers();
    } catch (error) {
      console.error('Buy X Get Y Auto Add Error:', error);
    } finally {
      this.isProcessing = false;
    }
  }

  async fetchCart() {
    try {
      const response = await fetch('/cart.js');
      this.cart = await response.json();
    } catch (error) {
      console.error('Failed to fetch cart:', error);
      throw error;
    }
  }

  async checkCartForOffers() {
    await this.fetchCart();
    await this.processOffers();
  }

  async processOffers() {
    if (!this.cart || !this.config.offers || this.cart.items.length === 0) {
      if (this.config.settings.debug) {
        console.log('Buy X Get Y: No offers to process', {
          cart: !!this.cart,
          offers: !!this.config.offers,
          cartItems: this.cart?.items?.length || 0
        });
      }
      return;
    }

    if (this.config.settings.debug) {
      console.log('Buy X Get Y: Processing offers', {
        offers: this.config.offers.length,
        cartItems: this.cart.items.length
      });
    }

    for (const offer of this.config.offers) {
      if (offer.enabled) {
        await this.processOffer(offer);
      }
    }
  }

  async processOffer(offer) {
    const { condition, reward, id, max_uses_per_order = 0 } = offer;
    
    if (this.config.settings.debug) {
      console.log('Buy X Get Y: Processing offer', offer);
    }
    
    // Check if condition is met
    const conditionMet = this.checkCondition(condition);
    
    if (this.config.settings.debug) {
      console.log('Buy X Get Y: Condition met?', conditionMet, 'for offer', id);
    }
    
    if (conditionMet) {
      // Calculate how many times this offer has already been applied
      const currentUses = this.getCurrentOfferUses(offer);
      
      if (this.config.settings.debug) {
        console.log('Buy X Get Y: Current offer uses:', currentUses, 'Max uses:', max_uses_per_order);
      }
      
      // Calculate how many qualifying items we have
      const qualifyingItems = this.getQualifyingItems(condition);
      const totalQualifyingQuantity = qualifyingItems.reduce((sum, item) => sum + item.quantity, 0);
      
      // Calculate how many times the offer should be applied based on qualifying items
      const timesOfferShouldBeApplied = Math.floor(totalQualifyingQuantity / condition.value);
      
      // Apply maximum uses limit if set (0 means unlimited)
      const maxAllowedUses = max_uses_per_order > 0 ? max_uses_per_order : timesOfferShouldBeApplied;
      const targetUses = Math.min(timesOfferShouldBeApplied, maxAllowedUses);
      
      if (this.config.settings.debug) {
        console.log('Buy X Get Y: Offer calculation:', {
          totalQualifyingQuantity,
          conditionValue: condition.value,
          timesOfferShouldBeApplied,
          maxAllowedUses,
          targetUses,
          currentUses
        });
      }
      
      // Check if we need to adjust the reward quantity
      if (currentUses < targetUses) {
        // Need to add more free gifts
        const additionalUses = targetUses - currentUses;
        const additionalQuantity = additionalUses * reward.quantity;
        
        if (this.config.settings.debug) {
          console.log('Buy X Get Y: Adding additional reward to cart', {
            additionalUses,
            additionalQuantity,
            reward
          });
        }
        
        await this.addRewardToCart({...reward, quantity: additionalQuantity}, offer);
      } else if (currentUses > targetUses) {
        // Need to remove excess free gifts
        const excessUses = currentUses - targetUses;
        
        if (this.config.settings.debug) {
          console.log('Buy X Get Y: Removing excess reward from cart', {
            excessUses,
            currentUses,
            targetUses
          });
        }
        
        await this.adjustRewardQuantity(reward, offer, targetUses * reward.quantity);
      } else {
        if (this.config.settings.debug) {
          console.log('Buy X Get Y: Reward quantity is correct');
        }
      }
    } else {
      // Only remove reward if it was added by this offer AND the qualifying items are no longer in cart
      const rewardInCart = await this.isRewardInCart(reward);
      if (rewardInCart) {
        // Check if the reward was added by this specific offer
        const rewardItem = this.cart.items.find(item => {
          const { product_handle, variant_id } = reward;
          let productId = null;
          // We need to check if this item matches our reward
          return item.properties && 
                 item.properties._buy_x_get_y_offer === offer.id &&
                 item.properties._free_gift === 'true';
        });
        
        if (rewardItem) {
          // Check if any qualifying items are still in cart (regardless of threshold)
          const matchingItemsInCart = this.getMatchingItemsOnly(condition);
          
          if (matchingItemsInCart.length === 0) {
            if (this.config.settings.debug) {
              console.log('Buy X Get Y: No qualifying items left, removing reward');
            }
            await this.removeRewardFromCart(reward, offer);
          } else {
            if (this.config.settings.debug) {
              console.log('Buy X Get Y: Qualifying items still in cart, keeping reward');
            }
          }
        }
      }
    }
  }

  checkCondition(condition) {
    const { type, value, products, collections, variants } = condition;
    
    switch (type) {
      case 'quantity':
        return this.checkQuantityCondition(products, collections, variants, value);
      case 'amount':
        return this.checkAmountCondition(products, collections, variants, value);
      case 'product':
        return this.checkProductCondition(products, value);
      case 'variant':
        return this.checkVariantCondition(variants, value);
      default:
        return false;
    }
  }

  getMatchingItemsOnly(condition) {
    const { products, collections, variants } = condition;
    
    if (this.config.settings.debug) {
      console.log('Buy X Get Y: Getting matching items only (no threshold check)', { products, collections, variants, cartItems: this.cart.items });
    }
    
    // Get all items that match the condition (excluding free gifts) - no threshold check
    const matchingItems = this.cart.items.filter(item => {
      // Exclude free gifts from qualifying items
      if (item.properties && item.properties._free_gift === 'true') {
        return false;
      }
      
      return this.itemMatchesCondition(item, products, collections, variants);
    });
    
    if (this.config.settings.debug) {
      console.log('Buy X Get Y: Found matching items', matchingItems.length);
    }
    
    return matchingItems;
  }

  getQualifyingItems(condition) {
    const { type, value, products, collections, variants } = condition;
    
    if (this.config.settings.debug) {
      console.log('Buy X Get Y: Getting qualifying items', { type, value, products, collections, variants, cartItems: this.cart.items });
    }
    
    // Get all items that match the condition (excluding free gifts)
    const matchingItems = this.cart.items.filter(item => {
      // Exclude free gifts from qualifying items
      if (item.properties && item.properties._free_gift === 'true') {
        return false;
      }
      
      return this.itemMatchesCondition(item, products, collections, variants);
    });
    
    // For amount conditions, check if the total amount meets the threshold
    if (type === 'amount') {
      const totalAmount = matchingItems.reduce((sum, item) => sum + item.final_line_price, 0);
      if (this.config.settings.debug) {
        console.log('Buy X Get Y: Amount condition check', { totalAmount, requiredAmount: value, meetsThreshold: totalAmount >= value });
      }
      
      // Only return items if the amount threshold is met
      return totalAmount >= value ? matchingItems : [];
    }
    
    // For quantity conditions, check if the total quantity meets the threshold
    if (type === 'quantity') {
      const totalQuantity = matchingItems.reduce((sum, item) => sum + item.quantity, 0);
      if (this.config.settings.debug) {
        console.log('Buy X Get Y: Quantity condition check', { totalQuantity, requiredQuantity: value, meetsThreshold: totalQuantity >= value });
      }
      
      // Only return items if the quantity threshold is met
      return totalQuantity >= value ? matchingItems : [];
    }
    
    // For product and variant conditions, return all matching items
    return matchingItems;
  }

  checkQuantityCondition(products, collections, variants, requiredQuantity) {
    let totalQuantity = 0;
    
    if (this.config.settings.debug) {
      console.log('Buy X Get Y: Checking quantity condition', {
        requiredQuantity,
        products,
        collections,
        variants,
        cartItems: this.cart.items
      });
    }
    
    for (const item of this.cart.items) {
      // Exclude free gifts from qualifying items
      if (item.properties && item.properties._free_gift === 'true') {
        if (this.config.settings.debug) {
          console.log('Buy X Get Y: Skipping free gift item', {
            item: {
              id: item.id,
              product_id: item.product_id,
              variant_id: item.variant_id,
              handle: item.handle,
              quantity: item.quantity
            }
          });
        }
        continue;
      }
      
      const matches = this.itemMatchesCondition(item, products, collections, variants);
      if (this.config.settings.debug) {
        console.log('Buy X Get Y: Item matches?', {
          item: {
            id: item.id,
            product_id: item.product_id,
            variant_id: item.variant_id,
            handle: item.handle,
            quantity: item.quantity
          },
          collections: collections,
          variants: variants,
          matches
        });
      }
      if (matches) {
        totalQuantity += item.quantity;
      }
    }
    
    if (this.config.settings.debug) {
      console.log('Buy X Get Y: Total quantity found', totalQuantity, 'required', requiredQuantity);
    }
    
    return totalQuantity >= requiredQuantity;
  }

  checkAmountCondition(products, collections, variants, requiredAmount) {
    let totalAmount = 0;
    
    for (const item of this.cart.items) {
      // Exclude free gifts from qualifying items
      if (item.properties && item.properties._free_gift === 'true') {
        continue;
      }
      
      if (this.itemMatchesCondition(item, products, collections, variants)) {
        totalAmount += item.final_line_price;
      }
    }
    
    return totalAmount >= requiredAmount;
  }

  checkProductCondition(products, requiredQuantity) {
    let totalQuantity = 0;
    
    for (const item of this.cart.items) {
      // Exclude free gifts from qualifying items
      if (item.properties && item.properties._free_gift === 'true') {
        continue;
      }
      
      if (products.includes(item.product_id.toString())) {
        totalQuantity += item.quantity;
      }
    }
    
    return totalQuantity >= requiredQuantity;
  }

  checkVariantCondition(variants, requiredQuantity) {
    let totalQuantity = 0;
    
    if (this.config.settings.debug) {
      console.log('Buy X Get Y: Checking variant condition', {
        variants,
        requiredQuantity,
        cartItems: this.cart.items
      });
    }
    
    for (const item of this.cart.items) {
      // Exclude free gifts from qualifying items
      if (item.properties && item.properties._free_gift === 'true') {
        continue;
      }
      
      // Convert both to strings for comparison to handle string vs number mismatch
      const itemVariantIdStr = String(item.variant_id);
      const matchingVariant = variants.find(v => String(v) === itemVariantIdStr);
      
      if (this.config.settings.debug) {
        console.log('Buy X Get Y: Checking variant in condition', {
          itemVariantId: item.variant_id,
          itemVariantIdStr,
          variants,
          matchingVariant,
          match: !!matchingVariant
        });
      }
      
      if (variants && matchingVariant) {
        totalQuantity += item.quantity;
        if (this.config.settings.debug) {
          console.log('Buy X Get Y: Found matching variant item', {
            item: {
              id: item.id,
              variant_id: item.variant_id,
              quantity: item.quantity
            },
            totalQuantity
          });
        }
      }
    }
    
    if (this.config.settings.debug) {
      console.log('Buy X Get Y: Variant condition result', {
        totalQuantity,
        requiredQuantity,
        conditionMet: totalQuantity >= requiredQuantity
      });
    }
    
    return totalQuantity >= requiredQuantity;
  }

  itemMatchesCondition(item, products, collections, variants) {
    if (this.config.settings.debug) {
      console.log('Buy X Get Y: Checking item condition', {
        item: {
          id: item.id,
          product_id: item.product_id,
          variant_id: item.variant_id,
          handle: item.handle
        },
        products,
        collections,
        variants
      });
    }
    
    // Check if item matches product IDs (always checked)
    if (products && products.length > 0) {
      if (products.includes(item.product_id.toString())) {
        if (this.config.settings.debug) {
          console.log('Buy X Get Y: Item matches product ID');
        }
        return true;
      }
    }
    
    // Check if item matches specific variant IDs (when variants are specified)
    if (variants && variants.length > 0) {
      // Convert both to strings for comparison to handle string vs number mismatch
      const itemVariantIdStr = String(item.variant_id);
      const variantIdStr = String(variants.find(v => String(v) === itemVariantIdStr));
      
      if (this.config.settings.debug) {
        console.log('Buy X Get Y: Checking variant match', {
          itemVariantId: item.variant_id,
          itemVariantIdStr,
          variants,
          variantIdStr,
          match: variantIdStr !== 'undefined'
        });
      }
      
      if (variantIdStr !== 'undefined') {
        if (this.config.settings.debug) {
          console.log('Buy X Get Y: Item matches variant ID', item.variant_id);
        }
        return true;
      }
    }
    
    // Check if item matches collection IDs using DOM data (when collections are specified)
    if (collections && collections.length > 0) {
      // Find the cart item element in the DOM to get collection data
      const cartItemElement = document.querySelector(`[data-product-id="${item.product_id}"]`);
      if (this.config.settings.debug) {
        console.log('Buy X Get Y: cartItemElement', {
          cart_element: cartItemElement,
          item_product_id: item.product_id
        });
      }
      if (cartItemElement) {
        const itemCollections = cartItemElement.getAttribute('data-collections');
        
        if (this.config.settings.debug) {
          console.log('Buy X Get Y: Item collections from DOM', {
            product_id: item.product_id,
            collections: itemCollections
          });
        }
        
        if (itemCollections) {
          const itemCollectionIds = itemCollections.split(',').map(id => id.trim());
          if (this.config.settings.debug) {
            console.log('Buy X Get Y: Item collection IDs', itemCollectionIds, collections);
          }
          for (const collectionId of collections) {
            // Convert both to strings for comparison to handle string vs number mismatch
            const collectionIdStr = String(collectionId);
            if (itemCollectionIds.includes(collectionIdStr)) {
              if (this.config.settings.debug) {
                console.log('Buy X Get Y: Item matches collection ID', collectionId);
              }
              return true;
            }
          }
        }
      } else {
        if (this.config.settings.debug) {
          console.warn('Buy X Get Y: Cart item element not found in DOM for product', item.product_id);
        }
        
        // Fallback: If DOM element not found, assume it matches if it's not a free gift
        // This prevents removing rewards when DOM is not ready
        // if (!item.properties || item.properties._free_gift !== 'true') {
        //   if (this.config.settings.debug) {
        //     console.log('Buy X Get Y: Using fallback - assuming item matches (not a free gift)');
        //   }
        //   return true;
        // }
      }
    }
    
    if (this.config.settings.debug) {
      console.log('Buy X Get Y: Item does not match condition');
    }
    
    return false;
  }

  async isRewardInCart(reward) {
    const { product_handle, variant_id } = reward;
    
    // For variant-only rewards (no product_handle), check by variant_id only
    if (variant_id && !product_handle) {
      return this.cart.items.some(item => 
        item.variant_id === variant_id &&
        item.properties && 
        item.properties._free_gift === 'true'
      );
    }
    
    // For product-based rewards, get product ID from handle
    let productId = null;
    if (product_handle) {
      try {
        const response = await fetch(`/products/${product_handle}.js`);
        if (response.ok) {
          const product = await response.json();
          productId = product.id;
        }
      } catch (error) {
        console.warn('Failed to get product ID from handle:', error);
        return false;
      }
    }
    
    if (!productId && !variant_id) return false;
    
    return this.cart.items.some(item => {
      const productMatch = productId ? item.product_id === productId : true;
      const variantMatch = variant_id ? item.variant_id === variant_id : true;
      const isFreeGift = item.properties && item.properties._free_gift === 'true';
      
      return productMatch && variantMatch && isFreeGift;
    });
  }

  async addRewardToCart(reward, offer) {
    try {
      const { product_handle, variant_id, quantity = 1 } = reward;
      
      // For variant-only rewards, use the variant_id directly
      let finalVariantId = variant_id;
      
      // For product-based rewards, get the first available variant if no variant_id provided
      if (!finalVariantId || finalVariantId === 0) {
        if (product_handle) {
          finalVariantId = await this.getFirstAvailableVariant(product_handle);
        } else {
          console.error('No variant ID or product handle available for reward:', reward);
          return;
        }
      }
      
      if (!finalVariantId) {
        console.error('No variant ID available for product:', product_handle);
        return;
      }
      
      // Use the EXACT same approach as AddToCart class in global.js
      const miniCart = document.querySelector('mini-cart');
      const sections = miniCart ? miniCart.getSectionsToRender().map((section) => section.id) : [];

      const body = JSON.stringify({
        id: finalVariantId,
        quantity: quantity,
        sections: sections,
        sections_url: window.location.pathname,
        properties: {
          '_buy_x_get_y_offer': offer.id,
          '_free_gift': 'true'
        }
      });

      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: body
      });

      if (response.ok) {
        const responseData = await response.json();
        
        if (this.config.settings.debug) {
          console.log('Buy X Get Y: Cart add response', responseData);
        }
        
        
        // Use the EXACT same approach as AddToCart class - call miniCart.renderContents directly
        if (miniCart && miniCart.renderContents && responseData.sections) {
          if (this.config.settings.debug) {
            console.log('Buy X Get Y: Updating mini cart with response data', responseData.sections);
          }
          miniCart.renderContents(responseData);
        } else {
          if (this.config.settings.debug) {
            console.log('Buy X Get Y: Mini cart not found, renderContents method missing, or no sections in response', {
              miniCart: !!miniCart,
              renderContents: !!(miniCart && miniCart.renderContents),
              sections: !!responseData.sections
            });
          }
        }
        
        // Also update main cart items if on cart page (same as cart.js)
        // const cartItems = document.querySelector('cart-items');
        // if (cartItems && cartItems.renderContents && responseData.sections) {
        //   if (this.config.settings.debug) {
        //     console.log('Buy X Get Y: Updating main cart items with response data', responseData.sections);
        //   }
        //   cartItems.renderContents(responseData);
        // } else {
        //   if (this.config.settings.debug) {
        //     console.log('Buy X Get Y: Main cart items not found, renderContents method missing, or no sections in response', {
        //       cartItems: !!cartItems,
        //       renderContents: !!(cartItems && cartItems.renderContents),
        //       sections: !!responseData.sections
        //     });
        //   }
        // }
        
        // Trigger the EXACT same events as AddToCart
        // document.dispatchEvent(new CustomEvent('ajaxProduct:added', {
        //   detail: {
        //     product: responseData
        //   }
        // }));
        
        // Refresh cart data to ensure it's in sync
        // setTimeout(() => {
        //   this.fetchCart();
        // }, 100);
        
      } else {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to add reward to cart');
      }
    } catch (error) {
      console.error('Error adding reward to cart:', error);
    }
  }

  async removeRewardFromCart(reward, offer) {
    try {
      const { product_handle, variant_id } = reward;
      
      // For variant-only rewards, find items by variant_id only
      let itemsToRemove = [];
      
      if (variant_id && !product_handle) {
        // Variant-only reward removal
        itemsToRemove = this.cart.items.filter(item => 
          item.variant_id === variant_id &&
          item.properties && 
          item.properties._buy_x_get_y_offer === offer.id
        );
      } else {
        // Product-based reward removal
        let productId = null;
        if (product_handle) {
          try {
            const response = await fetch(`/products/${product_handle}.js`);
            if (response.ok) {
              const product = await response.json();
              productId = product.id;
            }
          } catch (error) {
            console.warn('Failed to get product ID from handle for removal:', error);
            return;
          }
        }
        
        if (!productId) return;
        
        itemsToRemove = this.cart.items.filter(item => 
          item.product_id === productId && 
          (variant_id ? item.variant_id === variant_id : true) &&
          item.properties && 
          item.properties._buy_x_get_y_offer === offer.id
        );
      }

      if (itemsToRemove.length > 0) {
        const updates = {};
        itemsToRemove.forEach(item => {
          updates[item.key] = 0;
        });

        // Use the EXACT same approach as cart.js updateQuantity method
        const cartItems = document.querySelector('cart-items');
        const sections = cartItems ? cartItems.getSectionsToRender().map((section) => section.section) : [];

        const body = JSON.stringify({
          updates: updates,
          sections: sections,
          sections_url: window.location.pathname
        });

        const response = await fetch('/cart/update.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: body
        });

        if (response.ok) {
          const responseData = await response.json();
          
          // Use the EXACT same approach as cart.js - update sections directly
          if (cartItems && responseData.sections) {
            cartItems.getSectionsToRender().forEach((section => {
              const element = document.getElementById(section.id);
              if (element) {
                const elementToReplace = element.querySelector(section.selector) || element;
                if (elementToReplace && responseData.sections[section.section]) {
                  elementToReplace.innerHTML = cartItems.getSectionInnerHTML(responseData.sections[section.section], section.selector);
                }
              }
            }));
          }
          
          // Trigger the same events as cart.js
          document.dispatchEvent(new CustomEvent('cart:updated', {
            detail: {
              cart: JSON.stringify(responseData)
            }
          }));
          publish(PUB_SUB_EVENTS.cartUpdate, { source: 'cart-items' });
        }
      }
    } catch (error) {
      console.error('Error removing reward from cart:', error);
    }
  }

  getCurrentOfferUses(offer) {
    const { reward, id } = offer;
    let totalUses = 0;
    
    // Find all cart items that were added by this specific offer
    const offerItems = this.cart.items.filter(item => 
      item.properties && 
      item.properties._buy_x_get_y_offer === id &&
      item.properties._free_gift === 'true'
    );
    
    // Calculate total uses based on quantity of free gift items
    for (const item of offerItems) {
      totalUses += item.quantity;
    }
    
    if (this.config.settings.debug) {
      console.log('Buy X Get Y: Current offer uses calculation', {
        offerId: id,
        offerItems: offerItems.length,
        totalUses
      });
    }
    
    return totalUses;
  }

  async adjustRewardQuantity(reward, offer, targetQuantity) {
    try {
      const { product_handle, variant_id } = reward;
      
      // Find all items added by this offer
      const offerItems = this.cart.items.filter(item => 
        item.properties && 
        item.properties._buy_x_get_y_offer === offer.id &&
        item.properties._free_gift === 'true'
      );
      
      if (offerItems.length === 0) return;
      
      // Calculate current total quantity
      const currentQuantity = offerItems.reduce((sum, item) => sum + item.quantity, 0);
      
      if (currentQuantity === targetQuantity) {
        if (this.config.settings.debug) {
          console.log('Buy X Get Y: Quantity is already correct');
        }
        return;
      }
      
      // If we need to reduce quantity, remove items
      if (currentQuantity > targetQuantity) {
        const quantityToRemove = currentQuantity - targetQuantity;
        
        // Sort items by quantity (remove from items with highest quantity first)
        const sortedItems = [...offerItems].sort((a, b) => b.quantity - a.quantity);
        
        let remainingToRemove = quantityToRemove;
        const updates = {};
        
        for (const item of sortedItems) {
          if (remainingToRemove <= 0) break;
          
          const quantityFromThisItem = Math.min(item.quantity, remainingToRemove);
          const newQuantity = item.quantity - quantityFromThisItem;
          
          updates[item.key] = newQuantity;
          remainingToRemove -= quantityFromThisItem;
        }
        
        if (this.config.settings.debug) {
          console.log('Buy X Get Y: Adjusting reward quantity (removing)', {
            currentQuantity,
            targetQuantity,
            quantityToRemove,
            updates
          });
        }
        
        // Update cart with new quantities
        const cartItems = document.querySelector('cart-items');
        const sections = cartItems ? cartItems.getSectionsToRender().map((section) => section.section) : [];

        const body = JSON.stringify({
          updates: updates,
          sections: sections,
          sections_url: window.location.pathname
        });

        const response = await fetch('/cart/update.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: body
        });

        if (response.ok) {
          const responseData = await response.json();
          
          // Update sections directly
          if (cartItems && responseData.sections) {
            cartItems.getSectionsToRender().forEach((section => {
              const element = document.getElementById(section.id);
              if (element) {
                const elementToReplace = element.querySelector(section.selector) || element;
                if (elementToReplace && responseData.sections[section.section]) {
                  elementToReplace.innerHTML = cartItems.getSectionInnerHTML(responseData.sections[section.section], section.selector);
                }
              }
            }));
          }
          
          // Trigger events
          document.dispatchEvent(new CustomEvent('cart:updated', {
            detail: {
              cart: JSON.stringify(responseData)
            }
          }));
          publish(PUB_SUB_EVENTS.cartUpdate, { source: 'cart-items' });
        }
      }
      // If we need to increase quantity, add more items
      else if (currentQuantity < targetQuantity) {
        const quantityToAdd = targetQuantity - currentQuantity;
        
        if (this.config.settings.debug) {
          console.log('Buy X Get Y: Adding more reward quantity', {
            currentQuantity,
            targetQuantity,
            quantityToAdd
          });
        }
        
        await this.addRewardToCart({...reward, quantity: quantityToAdd}, offer);
      }
    } catch (error) {
      console.error('Error adjusting reward quantity:', error);
    }
  }

  async getFirstAvailableVariant(productHandle) {
    try {
      // Get product data to find first available variant using handle
      const response = await fetch(`/products/${productHandle}.js`);
      
      if (!response.ok) {
        console.error(`Product ${productHandle} not found (${response.status}):`, response.statusText);
        return null;
      }
      
      const product = await response.json();
      
      if (this.config.settings.debug) {
        console.log('Buy X Get Y: Product data fetched', {
          productHandle,
          product: {
            id: product.id,
            title: product.title,
            handle: product.handle,
            variants: product.variants?.length || 0
          }
        });
      }
      
      if (product.variants && product.variants.length > 0) {
        // Find first available variant
        const availableVariant = product.variants.find(variant => variant.available);
        const variantId = availableVariant ? availableVariant.id : product.variants[0].id;
        
        if (this.config.settings.debug) {
          console.log('Buy X Get Y: Selected variant', {
            variantId,
            available: availableVariant ? true : false
          });
        }
        
        return variantId;
      }
    } catch (error) {
      console.error('Failed to get product variants:', error);
    }
    return null;
  }

  getSectionsToRender() {
    // Get the sections that need to be updated (similar to cart.js)
    let sections = [
      {
        id: 'mini-cart',
        section: document.getElementById('mini-cart')?.id,
        selector: '.shopify-section',
      },
      {
        id: 'main-cart-items',
        section: document.getElementById('main-cart-items')?.dataset.id,
        selector: '.js-contents',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section'
      },
      {
        id: 'mobile-cart-icon-bubble',
        section: 'mobile-cart-icon-bubble',
        selector: '.shopify-section'
      },
      {
        id: 'cart-live-region-text',
        section: 'cart-live-region-text',
        selector: '.shopify-section'
      },
      {
        id: 'main-cart-footer',
        section: document.getElementById('main-cart-footer')?.dataset.id,
        selector: '.js-contents',
      }
    ];
    return sections;
  }

  // Cleanup method to disconnect MutationObserver
  destroy() {
    if (this.mutationObserver) {
      this.mutationObserver.disconnect();
      if (this.config.settings.debug) {
        console.log('Buy X Get Y: MutationObserver disconnected');
      }
    }
    if (this.mutationTimeout) {
      clearTimeout(this.mutationTimeout);
    }
  }
}

// Initialize when DOM is ready
document.addEventListener('DOMContentLoaded', () => {
  window.BuyXGetYAutoAdd.instance = new BuyXGetYAutoAdd();
});

// Export for potential external use
window.BuyXGetYAutoAdd = BuyXGetYAutoAdd;